// src/components/Auth/CategorySelection.js - Updated for multiple business profiles
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase/config";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useUserContext } from "../../contexts/UserContext"; // Importação do contexto
import "./CategorySelection.css";

const CategorySelection = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [businessModel, setBusinessModel] = useState("");
  // Novo estado para o modo de categorização - valor padrão "detailed"
  const [categorizationMode, setCategorizationMode] = useState("detailed");
  
  // Hook para acessar dados de assinatura e tipo de conta do contexto
  const { 
    user: contextUser, 
    subscription, 
    hasAccess, 
    activeAccountType, // Tipo de conta ativa atual (business ou personal)
    activeBusinessProfileId, // ID do perfil empresarial ativo
    getAccountSubpath // Função para obter o subcaminho correto
  } = useUserContext();

  // Obter a estrutura de categorias financeiras com base no modelo de negócio
  const getFinancialCategories = () => {
    // Modelo Pessoal - estrutura diferente
    if (businessModel === "pessoal" || businessModel === "pessoalComFilhos") {
      return {
        "💸 Receitas": {
          order: 1,
          displayName: "💸 Receitas",
          items: [
            "Salário",
            "Freelance",
            "Renda extra",
            "Reembolsos",
            "Outros Depósitos"
          ]
        },
        "🧾 Despesas Fixas": {
          order: 2,
          displayName: "🧾 Despesas Fixas",
          items: [
            "Aluguel/Financiamento",
            "Condomínio",
            "Energia",
            "Água",
            "Internet/Telefone",
            "Academia",
            "Assinaturas",
            "Plano de Saúde",
            "Planos Móveis",
            "Cursos",
            "Impostos",
            ...(businessModel === "pessoalComFilhos" ? [
              "Educação (Filhos)",
              "Higiene (Filhos)",
              "Cuidados e Apoio (Filhos)"
            ] : [])
          ]
        },
        "🍞 Despesas Variáveis": {
          order: 3,
          displayName: "🍞 Despesas Variáveis",
          items: [
            "Supermercado",
            "Farmácia",
            "Consultas",
            "Transporte Público",
            "Transporte por Aplicativo",
            "Gasolina",
            "Restaurantes/Lanches",
            "Lazer",
            "Roupas",
            ...(businessModel === "pessoalComFilhos" ? [
              "Roupas (Filhos)",
              "Brinquedos (Filhos)",
            ] : []),
            "Cuidados pessoais",
            "Eletrônicos e Eletrodomésticos",
            "Casa e Decoração",
            "Livros, Papelaria e Presentes",
            "Viagem",
            "Manutenção",
            "Instrumentos",
            "Apostas",
            "Outros Gastos"
          ]
        },
        "💳 Pagamentos e Transferências": {
          order: 4,
          displayName: "💳 Pagamentos e Transferências",
          items: [
            "Investimentos",
            "Pagamentos de empréstimos",
            "Tarifa bancária"
          ]
        }
      };
    }
    
    // Modelo consultório odontológico (exclusivo para plano business_pro)
    if (businessModel === "consultorio") {
      return {
        "1. RECEITA": {
          order: 1,
          displayName: "RECEITA",
          items: [
            "Dinheiro",
            "Cheque",
            "Boleto",
            "Transferência",
            "Cartão de Crédito",
            "Cartão de Débito",
            "Pix",
            "TED",
            "Convênios",
            "Outras Entradas"
          ]
        },
        "2. (-) DEDUÇÕES DA RECEITA": {
          order: 2,
          displayName: "(-) DEDUÇÕES DA RECEITA",
          items: [
            "DAS",
            "ISS",
            "PIS/COFINS"
          ]
        },
        "3. (+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS": {
          order: 3,
          displayName: "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS",
          items: [
            "Resgate de Aplicação",
            "Empréstimo",
            "Aporte de Sócio",
            "Venda de Ativos"
          ]
        },
        "4. (-) CUSTOS DOS SERVIÇOS PRESTADOS (CSP)": {
          order: 4,
          displayName: "(-) CUSTOS DOS SERVIÇOS PRESTADOS (CSP)",
          items: [
            "Materiais",
            "EPIs",
            "Laboratórios de Prótese",
            "Radiografias e Exames",
            "Despesas com Técnicos Auxiliares",
            "Esterilização e Autoclave",
            "Descarte de Resíduos Biológicos"
          ]
        },
        "5. (-) DESPESAS OPERACIONAIS": {
          order: 5,
          displayName: "(-) DESPESAS OPERACIONAIS",
          items: [
            "Contabilidade",
            "Consultoria / Assessoria",
            "Advogado",
            "Sistema de Gestão",
            "Despesas Bancárias",
            "Despesas Financeiras",
            "Aluguel",
            "Condomínio",
            "Energia Elétrica",
            "Água / Esgoto",
            "Telefone / Internet",
            "Equipamentos de Informática",
            "Manutenção de Equipamentos",
            "Materiais de Escritório",
            "Material de Limpeza / Higiene",
            "Publicidade e Marketing",
            "Serviços de Gráfica",
            "Salários",
            "INSS",
            "FGTS",
            "Vale Transporte",
            "Férias e 13º",
            "Treinamentos",
            "Uniformes",
            "Exames Admissionais / Periódicos",
            "Outros Impostos",
            "Multas / Taxas Diversas"
          ]
        },
        "6. (-) DESPESAS COM SÓCIOS": {
          order: 6,
          displayName: "(-) DESPESAS COM SÓCIOS",
          items: [
            "Pró-labore",
            "Distribuição de Lucros",
            "Despesas de Sócios"
          ]
        },
        "7. (-) INVESTIMENTOS": {
          order: 7,
          displayName: "(-) INVESTIMENTOS",
          items: [
            "Compra de Equipamentos",
            "Reformas da Clínica",
            "Aquisição de Móveis",
            "Compra de Softwares Especializados",
            "Aplicações Financeiras",
            "Projetos de Expansão",
            "Outros Investimentos"
          ]
        }
      };
    }

    // Modelo imobiliária (exclusivo para plano business_pro)
    if (businessModel === "imobiliaria") {
      return {
        "1. RECEITA": {
          order: 1,
          displayName: "RECEITA",
          items: [
            "Aluguel Recebido",
            "Taxa de Administração",
            "Taxa de Corretagem",
            "Venda de Imóveis",
            "Comissões",
            "Honorários de Intermediação",
            "Receitas de Condomínio (repassadas)",
            "Outras Entradas"
          ]
        },
        "2. (-) DEDUÇÕES DA RECEITA": {
          order: 2,
          displayName: "(-) DEDUÇÕES DA RECEITA",
          items: [
            "DAS",
            "ISS",
            "IRRF sobre Comissões",
            "PIS/COFINS",
            "Outros Tributos Retidos"
          ]
        },
        "4. (+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS": {
          order: 3,
          displayName: "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS",
          items: [
            "Resgate de Aplicação",
            "Aporte de Sócio",
            "Multas Contratuais Recebidas",
            "Indenizações",
            "Receitas com Juros/Correções"
          ]
        },
        "5. (-) CUSTOS DAS OPERAÇÕES IMOBILIÁRIAS": {
          order: 4,
          displayName: "(-) CUSTOS DAS OPERAÇÕES IMOBILIÁRIAS",
          items: [
            "Comissão de Corretores",
            "Repasse a Proprietários",
            "Repasses de Condomínio",
            "Vistoria e Laudo Técnico",
            "Custos com Divulgação de Imóveis",
            "Honorários Jurídicos (Venda/Locação)",
            "Registro e Documentação",
            "Outros Custos Operacionais"
          ]
        },
        "7. (-) DESPESAS OPERACIONAIS": {
          order: 5,
          displayName: "(-) DESPESAS OPERACIONAIS",
          items: [
            "Contabilidade",
            "Consultoria / Assessoria",
            "Advogado",
            "Sistema de Gestão",
            "Despesa Bancária",
            "Despesa Financeira",
            "Cartório",
            "Publicidade e Propaganda",
            "Internet",
            "Telefone",
            "Energia Elétrica",
            "Água / Esgoto",
            "Condomínio (escritório)",
            "Aluguel do Escritório",
            "Materiais de Escritório",
            "Manutenção do Imóvel",
            "Limpeza / Higiene",
            "Equipamentos",
            "Licenças e Softwares",
            "Salários",
            "Vale Transporte",
            "Refeição Funcionário",
            "13º Salário",
            "Férias",
            "FGTS",
            "INSS",
            "Uniformes",
            "Treinamentos",
            "Outras Despesas RH",
            "Outros Impostos",
            "Taxas e Multas"
          ]
        },
        "8. (-) DESPESAS COM SÓCIOS": {
          order: 6,
          displayName: "(-) DESPESAS COM SÓCIOS",
          items: [
            "Pró-labore",
            "Distribuição de Lucros",
            "Despesas Pessoais dos Sócios"
          ]
        },
        "9. (-) INVESTIMENTOS": {
          order: 7,
          displayName: "(-) INVESTIMENTOS",
          items: [
            "Aquisição de Equipamentos",
            "Compra de Imóveis para Renda",
            "Reformas em Imóveis Próprios",
            "Aplicações Financeiras",
            "Compra de Softwares",
            "Despesas com Expansão",
            "Outros Investimentos"
          ]
        }
      };
    }

    // Modelo academia
    if (businessModel === "academia") {
      return {
        "1. RECEITA": {
          order: 1,
          displayName: "RECEITA",
          items: [
            "Mensalidades de Planos (mensal, trimestral, anual)",
            "Aulas Avulsas/Diárias",
            "Personal Trainer (se a academia gerencia)",
            "Venda de Produtos (suplementos, roupas, acessórios)",
            "Aluguel de Armários/Espaços",
            "Avaliações Físicas",
            "Outras Entradas"
          ]
        },
        "2. (-) DEDUÇÕES DA RECEITA": {
          order: 2,
          displayName: "(-) DEDUÇÕES DA RECEITA",
          items: [
            "DAS",
            "ISS",
            "ICMS (se houver venda de produtos)",
            "PIS/COFINS"
          ]
        },
        "4. (+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS": {
          order: 3,
          displayName: "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS",
          items: [
            "Resgate de Aplicação",
            "Empréstimo",
            "Aporte de Sócio"
          ]
        },
        "5. (-) CUSTOS DOS SERVIÇOS PRESTADOS (CSP)": {
          order: 4,
          displayName: "(-) CUSTOS DOS SERVIÇOS PRESTADOS (CSP)",
          items: [
            "Manutenção de Equipamentos de Ginástica",
            "Reposição de Materiais Esportivos",
            "Produtos de Limpeza para Área de Treino",
            "Licenciamento de Músicas/Direitos Autorais",
            "Custo com Professores/Instrutores (se contratados como serviço)",
            "Serviços de Terceiros",
            "Material para Avaliação Física",
            "Água e Insumos para Bebedouros"
          ]
        },
        "7. (-) DESPESAS OPERACIONAIS": {
          order: 5,
          displayName: "(-) DESPESAS OPERACIONAIS",
          items: [
            "Contabilidade",
            "Consultoria / Assessoria",
            "Advogado",
            "Seguro",
            "Sistema de Gestão",
            "Despesa Bancária",
            "Despesa Financeira",
            "Cartório",
            "Envio / Entrega",
            "Outras Despesas ADM",
            "Aluguel",
            "Condomínio",
            "Energia Elétrica",
            "Gás",
            "Água / Esgoto",
            "Internet",
            "Telefone e TV a Cabo",
            "Estacionamento",
            "Manutenção",
            "Móveis e Utensílios",
            "Dedetização",
            "Publicidade e Propaganda",
            "Serviços Gráficos",
            "Material de Escritório",
            "Limpeza / Higiene",
            "Materiais de Reposição",
            "Salário",
            "Adiantamento",
            "Free-lance / Taxa",
            "13º Salário",
            "Férias + Abono",
            "Rescisão Contratual",
            "Vale Transporte",
            "Exame Médico",
            "FGTS",
            "Contribuição Sindical",
            "Refeição Funcionário",
            "INSS",
            "Treinamento",
            "Uniforme",
            "Farmácia",
            "Locação de Equipamentos",
            "Outras Despesas RH",
            "Outros Impostos",
            "Taxas e Multas"
          ]
        },
        "8. (-) DESPESAS COM SÓCIOS": {
          order: 6,
          displayName: "(-) DESPESAS COM SÓCIOS",
          items: [
            "Despesas de Sócios",
            "Pró-labore"
          ]
        },
        "9. (-) INVESTIMENTOS": {
          order: 7,
          displayName: "(-) INVESTIMENTOS",
          items: [
            "Aquisição de Equipamentos de Ginástica",
            "Obras e Reformas Estruturais",
            "Aplicações Financeiras / Fundos",
            "Aquisição de Móveis Fixos",
            "Melhorias no Imóvel",
            "Compra de Sistemas / Softwares",
            "Despesas com Projetos de Expansão",
            "Outros Investimentos de Longo Prazo"
          ]
        }
      };
    }

    // Estrutura base para os modelos de negócio tradicionais
    const baseCategories = {
      "1. RECEITA": {
        order: 1,
        displayName: "RECEITA",
        items: [
          "Dinheiro",
          "Cheque",
          "Boleto",
          "Transferência",
          "Cartão de Crédito",
          "Cartão de Débito",
          "Pix",
          "TED",
          "Voucher",
          "Ifood",
          "Outras Entradas"
        ]
      },
      "2. (-) DEDUÇÕES DA RECEITA": {
        order: 2,
        displayName: "(-) DEDUÇÕES DA RECEITA",
        items: [
          "DAS",
          "ISS",
          "ICMS",
          "PIS/COFINS"
        ]
      },
      "4. (+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS": {
        order: 3,
        displayName: "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS",
        items: [
          "Resgate de Aplicação",
          "Empréstimo",
          "Aporte de Sócio"
        ]
      },
      "5. (-) CUSTOS DAS MERCADORIAS VENDIDAS (CMV)": {
        order: 4,
        displayName: "(-) CUSTOS DAS MERCADORIAS VENDIDAS (CMV)",
        items: [
          "Alimentos Básicos",
          "Doces",
          "Carnes",
          "Bebidas",
          "Vinho",
          "Chopp",
          "Hortifrúti",
          "Café",
          "Embalagem"
        ]
      },
      "7. (-) DESPESAS OPERACIONAIS": {
        order: 5,
        displayName: "(-) DESPESAS OPERACIONAIS",
        items: [
          "Contabilidade",
          "Consultoria / Assessoria",
          "Advogado",
          "Seguro",
          "Sistema de Gestão",
          "Despesa Bancária",
          "Despesa Financeira",
          "Cartório",
          "Envio / Entrega",
          "Outras Despesas ADM",
          "Aluguel",
          "Condomínio",
          "Energia Elétrica",
          "Gás",
          "Água / Esgoto",
          "Internet",
          "Telefone e TV a Cabo",
          "Estacionamento",
          "Equipamentos",
          "Informática",
          "Manutenção",
          "Móveis e Utensílios",
          "Dedetização",
          "Publicidade e Propaganda",
          "Serviços Gráficos",
          "Material de Escritório",
          "Limpeza / Higiene",
          "Materiais de Reposição",
          "Salário",
          "Adiantamento",
          "Free-lance / Taxa",
          "13º Salário",
          "Férias + Abono",
          "Rescisão Contratual",
          "Vale Transporte",
          "Exame Médico",
          "FGTS",
          "Contribuição Sindical",
          "Refeição Funcionário",
          "INSS",
          "Treinamento",
          "Uniforme",
          "Farmácia",
          "Artístico",
          "Locação de Equipamentos",
          "Outras Despesas RH",
          "Outros Impostos",
          "Taxas e Multas"
        ]
      },
      "8. (-) DESPESAS COM SÓCIOS": {
        order: 6,
        displayName: "(-) DESPESAS COM SÓCIOS",
        items: [
          "Despesas de Sócios",
          "Pró-labore"
        ]
      },
      "9. (-) INVESTIMENTOS": {
        order: 7,
        displayName: "(-) INVESTIMENTOS",
        items: [
          "Aquisição de Equipamentos Imobilizados",
          "Obras e Reformas Estruturais",
          "Aplicações Financeiras / Fundos",
          "Aquisição de Móveis Fixos",
          "Melhorias no Imóvel",
          "Compra de Sistemas / Softwares",
          "Despesas com Projetos de Expansão",
          "Outros Investimentos de Longo Prazo"
        ]
      }
    };

    // Se o modelo selecionado for Barbearia/Salão
    if (businessModel === "barbearia") {
      // Remover Ifood e VR da receita
      const receiptItems = baseCategories["1. RECEITA"].items;
      baseCategories["1. RECEITA"].items = receiptItems.filter(
        item => item !== "Ifood" && item !== "VR"
      );
      
      // Substituir itens do CMV por itens específicos para barbearia/salão
      baseCategories["5. (-) CUSTOS DAS MERCADORIAS VENDIDAS (CMV)"].items = [
        "Lâminas",
        "Tinturas",
        "Descolorantes",
        "Shampoos",
        "Espuma de barbear",
        "Loções",
        "Pomadas",
        "Óleos para barba",
        "Esmaltes"
      ];
    }
    
    // Se o modelo selecionado for Cafeteria
    if (businessModel === "cafeteria") {
      // Substituir itens do CMV por itens específicos para cafeteria
      baseCategories["5. (-) CUSTOS DAS MERCADORIAS VENDIDAS (CMV)"].items = [
        "Café",
        "Leite",
        "Doces",
        "Salgados",
        "Xaropes",
        "Bebidas",
        "Vinho",
        "Chopp",
        "Embalagem",
        "Insumos Alimentícios (geral)"
      ];
    }

    return baseCategories;
  };

  // Obter categorias atualizadas com base no modelo de negócio
  const financialCategories = getFinancialCategories();

  // Converter o objeto de categorias em um array para facilitar a navegação
  const categoryGroups = Object.keys(financialCategories);
  
  // Total de passos agora inclui:
  // 1. Seleção do modelo de negócio
  // 2. Seleção do modo de categorização (simplificado/detalhado)
  // 3. Etapas de seleção de categorias
  const totalSteps = businessModel
  ? categorizationMode
    ? categorizationMode === "simplified"
      ? 3 // Etapa 0: Modelo, Etapa 1: Modo, Etapa 2: Resumo Simplificado
      : categoryGroups.length + 2 // Etapa 0: Modelo, Etapa 1: Modo, Etapas 2 em diante: Grupos de Categoria
    : 2 // Etapa 0: Modelo, Etapa 1: Modo (modo ainda não escolhido)
  : 1; // Etapa 0: Modelo (modelo ainda não escolhido)
  
  // Efeito para resetar a posição de rolagem quando mudar de etapa
  useEffect(() => {
    // Rolar para o topo da página
    window.scrollTo(0, 0);
    
    // Também resetar a rolagem do container de categorias se ele tiver uma referência
    const categoriesContainer = document.querySelector('.category-list.single-page');
    if (categoriesContainer) {
      categoriesContainer.scrollTop = 0;
    }
  }, [currentStep]);

  // Efeito para monitorar mudanças na autenticação e carregar dados do usuário
  useEffect(() => {
    setLoading(true);
    
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        // Carregar categorias previamente selecionadas pelo usuário
        loadUserCategories(currentUser.uid);
      } else {
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [navigate, activeAccountType, activeBusinessProfileId]); // Adicionar activeBusinessProfileId como dependência
  
  // Função para carregar as categorias do usuário do Firestore - MODIFICADA para suportar perfis empresariais
  const loadUserCategories = async (userId) => {
    try {
      // Fazer a consulta ao Firestore para obter as categorias do usuário
      // com base no tipo de conta e no perfil ativo
      
      if (activeAccountType === 'personal') {
        // Para conta pessoal, usar o caminho já existente
        const userCategoriesDoc = await getDoc(doc(db, "userCategories", userId, "personal", "categories"));
        
        if (userCategoriesDoc.exists()) {
          const data = userCategoriesDoc.data();
          setSelectedCategories(data.categories || {});
          setBusinessModel(data.businessModel || "pessoal");
          setCategorizationMode(data.categorizationMode || "detailed");
        } else {
          setSelectedCategories({});
          setBusinessModel("pessoal");
          setCategorizationMode("detailed");
        }
      } else {
        // Para conta empresarial, usar o subcaminho correto com base no perfil ativo
        const subcollection = getAccountSubpath();
        
        console.log(`Carregando categorias do perfil empresarial: ${subcollection}`);
        
        // Primeiro, tentar carregar do caminho específico do perfil
        let categoriesDoc = null;
        
        if (subcollection === 'business') {
          // Para o perfil principal, verificar primeiro no novo caminho estruturado
          categoriesDoc = await getDoc(doc(db, "userCategories", userId, "business", "categories"));
          
          // Se não existir no novo caminho, verificar no caminho legado
          if (!categoriesDoc.exists()) {
            categoriesDoc = await getDoc(doc(db, "userCategories", userId));
          }
        } else {
          // Para perfis personalizados, usar o caminho com o ID do perfil
          categoriesDoc = await getDoc(doc(db, "userCategories", userId, subcollection, "categories"));
        }
        
        if (categoriesDoc && categoriesDoc.exists()) {
          const data = categoriesDoc.data();
          
          // Para categorias legadas, verificar se há businessCategories ou usar categories
          if (subcollection === 'business' && !data.categories) {
            setSelectedCategories(data.businessCategories || data.categories || {});
          } else {
            setSelectedCategories(data.categories || {});
          }
          
          setBusinessModel(data.businessModel || "restaurante");
          setCategorizationMode(data.categorizationMode || data.businessCategorizationMode || "detailed");
        } else {
          setSelectedCategories({});
          setBusinessModel("restaurante");
          setCategorizationMode("detailed");
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Erro ao carregar categorias do usuário:", error);
      setLoading(false);
    }
  };

  // Função para selecionar modelo de negócio
  const handleBusinessModelSelect = (model) => {
    setBusinessModel(model);
    // Resetar categorias selecionadas se mudar o modelo de negócio
    setSelectedCategories({});
    // Avançar para a próxima etapa (seleção de modo de categorização)
    setCurrentStep(1);
  };

  // Função para selecionar modo de categorização
  const handleCategorizationModeSelect = (mode) => {
    setCategorizationMode(mode);
    
    // Se o modo for simplificado, pré-selecionar todos os grupos de categorias
    if (mode === "simplified") {
      const simplifiedCategories = {};
      Object.keys(financialCategories).forEach(group => {
        const displayName = financialCategories[group].displayName;
        simplifiedCategories[`${displayName}.${displayName}`] = true;
      });
      setSelectedCategories(simplifiedCategories);
    } else {
      // Se mudar de simplificado para detalhado, resetar as categorias
      setSelectedCategories({});
    }
    
    // Avançar para a próxima etapa
    setCurrentStep(2);
  };

  // Função para alternar o estado de seleção de uma categoria
  const handleCategoryToggle = (category) => {
    // Calculamos o índice do grupo adequado porque agora temos 2 etapas anteriores
    const categoryIndex = currentStep - 2;
    const currentGroup = categoryGroups[categoryIndex];
    const displayName = financialCategories[currentGroup].displayName;
    
    setSelectedCategories(prev => {
      // Usar o nome sem o prefixo numérico para armazenamento
      const path = `${displayName}.${category}`;
      
      return {
        ...prev,
        [path]: !prev[path]
      };
    });
  };

  // Função para salvar categorias no Firestore - MODIFICADA para suportar perfis empresariais
  const saveCategories = async () => {
    if (!user) {
      console.error("Usuário não autenticado");
      alert("Você precisa estar logado para salvar categorias.");
      navigate("/login");
      return;
    }
    
    try {
      setSaving(true);
      
      // Verificar autenticação atual
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Sessão expirada. Faça login novamente.");
      }
      
      // Preparar os dados para salvar
      const categoriesToSave = {};
      
      // Processar categorias selecionadas
      Object.keys(selectedCategories).forEach(path => {
        if (selectedCategories[path]) {
          categoriesToSave[path] = true;
        }
      });

      // Armazenar metadata para ajudar na ordenação
      const categoryOrderData = {};
      Object.keys(financialCategories).forEach(groupKey => {
        const { displayName, order } = financialCategories[groupKey];
        categoryOrderData[displayName] = order;
      });

      // Obter o subcaminho correto para a conta ativa
      const subcollection = getAccountSubpath();
      console.log(`Salvando categorias para: ${activeAccountType}, perfil: ${subcollection}`);
      
      // Preparar os dados a serem salvos
      const categoriesData = {
        categories: categoriesToSave,
        categoryOrder: categoryOrderData,
        businessModel: businessModel,
        categorizationMode: categorizationMode,
        updatedAt: new Date()
      };
      
      if (activeAccountType === 'personal') {
        // Para conta pessoal, salvar na subcoleção "personal/categories"
        await setDoc(doc(db, "userCategories", currentUser.uid, "personal", "categories"), {
          ...categoriesData,
          createdAt: new Date()
        });
      } else {
        // Para conta empresarial, usar o caminho apropriado baseado no perfil ativo
        
        if (subcollection === 'business') {
          // Para o perfil principal/default (ID 'business'):
          
          // 1. Salvar no novo caminho estruturado
          await setDoc(doc(db, "userCategories", currentUser.uid, "business", "categories"), {
            ...categoriesData,
            createdAt: new Date()
          });
          
          // 2. Salvar também no caminho legado para compatibilidade
          await setDoc(doc(db, "userCategories", currentUser.uid), {
            categories: categoriesToSave,
            categoryOrder: categoryOrderData,
            businessCategories: categoriesToSave,
            businessCategoryOrder: categoryOrderData,
            businessModel: businessModel,
            businessCategorizationMode: categorizationMode,
            updatedAt: new Date(),
            createdAt: new Date()
          });
        } else {
          // Para outros perfis empresariais, usar o ID do perfil como subcoleção
          await setDoc(doc(db, "userCategories", currentUser.uid, subcollection, "categories"), {
            ...categoriesData,
            createdAt: new Date()
          });
        }
      }

      console.log("Categorias salvas com sucesso!");
      
      // Navegar para o dashboard
      navigate("/dashboard");
    } catch (error) {
      console.error("Erro ao salvar categorias:", error);
      
      let errorMessage = "Erro ao salvar suas categorias. Tente novamente.";
      
      if (error.code === "permission-denied") {
        errorMessage = "Permissão negada. Verifique as regras do Firestore.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Funções de navegação
  const handleNext = () => {
    // Se estivermos na última etapa das categorias ou no modo simplificado
    if ((currentStep >= totalSteps - 1) || 
        (categorizationMode === "simplified" && currentStep === 2)) {
      saveCategories();
    } else {
      setCurrentStep(prevStep => prevStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prevStep => prevStep - 1);
    }
  };

  // Função para verificar se uma categoria está selecionada, baseado nos dados do Firebase
  const isCategorySelected = (group, category) => {
    const displayName = financialCategories[group].displayName;
    const path = `${displayName}.${category}`;
    return !!selectedCategories[path];
  };

  if (loading) {
    return (
      <div className="category-selection-container">
        <div className="category-selection-card loading-card">
          <div className="loading-spinner"></div>
          <p>Carregando suas categorias...</p>
        </div>
      </div>
    );
  }

  // Determinar os modelos disponíveis baseado no plano e tipo de conta
  const getAvailableModels = () => {
    // Se o tipo de conta for pessoal, apenas mostrar modelos pessoais
    if (activeAccountType === 'personal') {
      return ['pessoal', 'pessoalComFilhos'];
    }
    
    // Para conta empresarial, verificar o plano
    if (!subscription || !subscription.active) {
      // Se não houver assinatura ativa, mostrar apenas modelos empresariais básicos
      return ['restaurante', 'barbearia', 'cafeteria', 'academia'];
    }
    
    // Se o plano for 'personal', não deve estar na visão empresarial
    if (subscription.planType === 'personal') {
      return ['restaurante', 'barbearia', 'cafeteria', 'academia'];
    }
    
    // Para planos 'business', mostrar os modelos de negócio básicos
    if (subscription.planType === 'business') {
      return ['restaurante', 'barbearia', 'cafeteria', 'academia'];
    }
    
    // Para plano 'business_pro', mostrar todos os modelos de negócio empresariais
    if (subscription.planType === 'business_pro') {
      return ['restaurante', 'barbearia', 'cafeteria', 'academia', 'consultorio', 'imobiliaria'];
    }
    
    // Por padrão, mostrar modelos empresariais básicos
    return ['restaurante', 'barbearia', 'cafeteria', 'academia'];
  };

  // Renderizar a seleção de modelo de negócio como etapa 0
  if (currentStep === 0) {
    const availableModels = getAvailableModels();
    
    return (
      <div className="category-selection-container">
        <div className="category-selection-card">
          <div className="progress-bar-container">
            <div className="progress-text">
              Etapa 1 de {totalSteps}
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${(1 / totalSteps) * 100}%` }}
              ></div>
            </div>
          </div>
          
          <div className="step-heading">
            <span className="step-number">1</span>
            <h2 className="category-group-title">
              {activeAccountType === 'personal' ? 'Tipo de Conta Pessoal' : 'Modelo de Negócio'}
            </h2>
          </div>
          
          <p className="instruction">
            {activeAccountType === 'personal' 
              ? 'Selecione o tipo de conta pessoal:' 
              : 'Selecione o tipo de negócio da sua empresa:'}
          </p>
          
          <div className="business-model-selection">
            {availableModels.includes('restaurante') && (
              <button 
                className={`business-model-button ${businessModel === 'restaurante' ? 'selected' : ''}`}
                onClick={() => handleBusinessModelSelect('restaurante')}
              >
                <div className="business-icon">🍽️</div>
                <div className="business-type">Restaurante/Bar</div>
              </button>
            )}
            
            {availableModels.includes('cafeteria') && (
              <button 
                className={`business-model-button ${businessModel === 'cafeteria' ? 'selected' : ''}`}
                onClick={() => handleBusinessModelSelect('cafeteria')}
              >
                <div className="business-icon">☕</div>
                <div className="business-type">Cafeteria</div>
              </button>
            )}
            
            {availableModels.includes('barbearia') && (
              <button 
                className={`business-model-button ${businessModel === 'barbearia' ? 'selected' : ''}`}
                onClick={() => handleBusinessModelSelect('barbearia')}
              >
                <div className="business-icon">✂️</div>
                <div className="business-type">Barbearia/Salão</div>
              </button>
            )}
            
            {availableModels.includes('academia') && (
              <button 
                className={`business-model-button ${businessModel === 'academia' ? 'selected' : ''}`}
                onClick={() => handleBusinessModelSelect('academia')}
              >
                <div className="business-icon">💪</div>
                <div className="business-type">Academia</div>
              </button>
            )}
            
            {availableModels.includes('consultorio') && (
              <button 
                className={`business-model-button ${businessModel === 'consultorio' ? 'selected' : ''}`}
                onClick={() => handleBusinessModelSelect('consultorio')}
              >
                <div className="business-icon">🩺</div>
                <div className="business-type">Consultório</div>
              </button>
            )}

            {availableModels.includes('imobiliaria') && (
              <button 
                className={`business-model-button ${businessModel === 'imobiliaria' ? 'selected' : ''}`}
                onClick={() => handleBusinessModelSelect('imobiliaria')}
              >
                <div className="business-icon">🏠</div>
                <div className="business-type">Imobiliária</div>
              </button>
            )}

            {availableModels.includes('pessoal') && (
              <button 
                className={`business-model-button ${businessModel === 'pessoal' ? 'selected' : ''}`}
                onClick={() => handleBusinessModelSelect('pessoal')}
              >
                <div className="business-icon">👤</div>
                <div className="business-type">Pessoal</div>
              </button>
            )}

            {availableModels.includes('pessoalComFilhos') && (
              <button 
                className={`business-model-button ${businessModel === 'pessoalComFilhos' ? 'selected' : ''}`}
                onClick={() => handleBusinessModelSelect('pessoalComFilhos')}
              >
                <div className="business-icon">👨‍👩‍👧‍👦</div>
                <div className="business-type">Pessoal com filho(s)</div>
              </button>
            )}
          </div>
          
          <div className="navigation-buttons">
            <button 
              className="previous-button" 
              onClick={handlePrevious} 
              disabled={true}
            >
              Anterior
            </button>
            
            <button 
              className="next-button" 
              onClick={handleNext}
              disabled={!businessModel}
            >
              Próximo
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Renderizar etapa de seleção de modo de categorização
  if (currentStep === 1) {
    return (
      <div className="category-selection-container">
        <div className="category-selection-card">
          <div className="progress-bar-container">
            <div className="progress-text">
              Etapa 2 de {totalSteps}
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${(2 / totalSteps) * 100}%` }}
              ></div>
            </div>
          </div>
          
          <div className="step-heading">
            <span className="step-number">2</span>
            <h2 className="category-group-title">
              Modo de Organização
            </h2>
          </div>
          
          <p className="instruction">
            Escolha como deseja organizar suas categorias financeiras:
          </p>
          
          <div className="business-model-selection">
            {/* Botão Detalhada primeiro */}
            <button 
              className={`business-model-button ${categorizationMode === 'detailed' ? 'selected' : ''}`}
              onClick={() => handleCategorizationModeSelect('detailed')}
            >
              <div className="business-icon">📋</div>
              <div className="business-type">
                <strong>Detalhada</strong>
                <p style={{ fontSize: '14px', margin: '5px 0 0 0', fontWeight: 'normal' }}>
                  Selecione categorias específicas dentro de cada grupo para um controle mais preciso
                </p>
              </div>
            </button>
            
            {/* Botão Simplificada depois */}
            <button 
              className={`business-model-button ${categorizationMode === 'simplified' ? 'selected' : ''}`}
              onClick={() => handleCategorizationModeSelect('simplified')}
            >
              <div className="business-icon">🔄</div>
              <div className="business-type">
                <strong>Simplificada</strong>
                <p style={{ fontSize: '14px', margin: '5px 0 0 0', fontWeight: 'normal' }}>
                  Utilize apenas os grupos principais de categorias sem precisar selecionar subcategorias
                </p>
              </div>
            </button>
          </div>
          
          <div className="navigation-buttons">
            <button 
              className="previous-button" 
              onClick={handlePrevious}
            >
              Anterior
            </button>
            
            <button 
              className="next-button" 
              onClick={handleNext}
              disabled={!categorizationMode}
            >
              Próximo
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Para modo simplificado, vamos diretamente para salvar
  if (categorizationMode === "simplified" && currentStep === 2) {
    return (
      <div className="category-selection-container">
        <div className="category-selection-card">
          <div className="progress-bar-container">
            <div className="progress-text">
              Etapa 3 de {totalSteps}
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `100%` }}
              ></div>
            </div>
          </div>
          
          <div className="step-heading">
            <span className="step-number">3</span>
            <h2 className="category-group-title">
              Resumo - Organização Simplificada
            </h2>
          </div>
          
          <p className="instruction">
            As seguintes categorias serão criadas para sua organização financeira:
          </p>
          
          <div className="categories-container">
            <div className="category-list single-page">
              {Object.keys(financialCategories).map((group, index) => {
                const displayName = financialCategories[group].displayName;
                
                return (
                  <div key={index} className="category-item">
                    <div className="checkbox-label" style={{ fontWeight: 'bold' }}>
                      {group}
                    </div>
                    <div style={{ marginLeft: '30px', color: '#666', fontSize: '14px' }}>
                      {financialCategories[group].items.slice(0, 3).join(", ")}
                      {financialCategories[group].items.length > 3 ? "..." : ""}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="navigation-buttons">
            <button 
              className="previous-button" 
              onClick={handlePrevious}
            >
              Anterior
            </button>
            
            <button 
              className="next-button" 
              onClick={handleNext}
              disabled={saving}
            >
              {saving ? "Salvando..." : "Finalizar"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Para as etapas de categoria (2 em diante), ajustar o índice para acessar o grupo correto
  const categoryIndex = currentStep - 2;
  const currentGroup = categoryGroups[categoryIndex];
  const currentCategories = financialCategories[currentGroup].items;

  return (
    <div className="category-selection-container">
      <div className="category-selection-card">
        <div className="progress-bar-container">
          <div className="progress-text">
            Etapa {currentStep} de {totalSteps - 1}
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            ></div>
          </div>
        </div>
        
        <div className="step-heading">
          <span></span>
          <h2 className="category-group-title">{currentGroup}</h2>
        </div>
      
        <p className="instruction">Selecione as opções que você deseja utilizar:</p>
        
        <div className="categories-container">
          <div className="category-list single-page">
            {currentCategories.map((category, index) => {
              // Para cada categoria, verificamos no estado selectedCategories carregado do Firebase
              const isSelected = isCategorySelected(currentGroup, category);
              return (
                <div key={index} className="category-item">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleCategoryToggle(category)}
                    />
                    {category}
                  </label>
                </div>
              );
            })}
          </div>
        </div>

        <div className="navigation-buttons">
          <button 
            className="previous-button" 
            onClick={handlePrevious} 
            disabled={currentStep === 0}
          >
            Anterior
          </button>
          
          <button 
            className="next-button" 
            onClick={handleNext}
            disabled={saving}
          >
            {currentStep === totalSteps - 1 
              ? (saving ? "Salvando..." : "Finalizar") 
              : "Próximo"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategorySelection;